import { NgIf } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatColumnDef, MatCellDef, MatCell, MatRowDef, MatRow } from '@angular/material/table';
import { AuthorizationSelectors } from '@next/core-lib/authorization';
import { Cart, CartActions, CartLineSources, CartSelectors } from '@next/core-lib/cart';
import { AbstractDialog, AbstractDialogData, AbstractDialogResultData, AgrDialogModule } from '@next/core-lib/dialog';
import { AgrEnvironment, EnvironmentInjector } from '@next/core-lib/environment';
import { FeatureFlagService } from '@next/core-lib/featureflag';
import { AgrFormModule } from '@next/core-lib/form';
import { LocaleService, AgrI18nSharedModule } from '@next/core-lib/i18n';
import { AllowedOrderRoles } from '@next/core-lib/models';
import { BreakpointService, BreakpointServiceInjector } from '@next/core-lib/responsive';
import { TableV2Module } from '@next/core-lib/table-v2';
import { UserSelectors } from '@next/core-lib/user';
import { UntilDestroy } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { map, switchMap, takeUntil } from 'rxjs/operators';

import { decorateFormControl, isItemTypeEligibleForCartType, isUserAllowedToOrderItem, skuIsOrderable } from 'src/app/product/helpers/add-to-cart-helpers';
import { openWizardDialogForFavorite } from 'src/app/product/store/actions';
import { ShopFeatureFlags } from 'src/app/shared/shop-feature-flags';
import { FavoriteSku } from 'src/app/standalone/favorites-star/favorite-sku.model';

import { FavoritesStarComponent } from 'src/app/standalone/favorites-star/favorites-star.component';

import { FavoritesDataSource } from './favorites.datasource';

export interface FavoritesDialogData extends AbstractDialogData {
  cart?: Cart;
  htmlButtonRef?: HTMLButtonElement;
}

@UntilDestroy({ checkProperties: true })
@Component({
  selector: 'app-favorites-dialog',
  templateUrl: './favorites-dialog.component.html',
  styleUrls: ['./favorites-dialog.component.scss'],
  imports: [
    AgrDialogModule,
    TableV2Module,
    MatColumnDef,
    MatCellDef,
    MatCell,
    NgIf,
    MatFormField,
    MatLabel,
    MatInput,
    AgrFormModule,
    FormsModule,
    ReactiveFormsModule,
    MatSuffix,
    FavoritesStarComponent,
    MatButton,
    MatIcon,
    MatRowDef,
    MatRow,
    AgrI18nSharedModule,
  ],
})
export class FavoritesDialogComponent extends AbstractDialog<FavoritesDialogData> implements OnInit, OnDestroy {
  hasAtLeastAdvisorRole = false;

  cartId: number;
  public isFavoritesEnabled = true;
  public readonly dataSource: FavoritesDataSource;
  public quantityForms = new FormArray([]);
  public tableColumns: string[] = ['name', 'quantity', 'favoriteStar', 'addToCart'];
  private readonly destroy$ = new Subject<void>();
  private readonly customer$ = this.store.select(UserSelectors.selectCustomer);

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: FavoritesDialogData,
    @Inject(BreakpointServiceInjector) readonly breakpoint: BreakpointService,
    @Inject(EnvironmentInjector) readonly environment: AgrEnvironment,
    protected mdDialogRef: MatDialogRef<FavoritesDialogComponent, AbstractDialogResultData<never>>,
    private readonly store: Store,
    private readonly localeService: LocaleService,
    private readonly featureFlagService: FeatureFlagService,
  ) {
    super();
    this.dataSource = new FavoritesDataSource(this.store);

    this.dataSource.connect().subscribe((favorites) => {
      this.quantityForms.clear();
      favorites.forEach((favorite) => {
        const formControl = new FormControl(favorite.orderingInformation?.minimumOrderUnitQuantity ?? 0);
        this.quantityForms.push(formControl);
        decorateFormControl(favorite, formControl, this.localeService);
      });
    });
  }

  ngOnInit(): void {
    this.customer$.pipe(
      takeUntil(this.destroy$),
      map((selectedCustomer) => selectedCustomer?.shipTos[0]?.salesAreas[0]?.salesOrganization),
      switchMap((salesOrganization) => this.featureFlagService.getFeatureFlagsForSalesArea(salesOrganization)),
      map((featureFlags) => (this.isFavoritesEnabled = featureFlags.includes(ShopFeatureFlags.enableFavorites))),
    );

    this.store
      .select(AuthorizationSelectors.hasAtLeastAdvisorRole)
      .subscribe((hasAtLeastAdvisorRuleValue) => (this.hasAtLeastAdvisorRole = hasAtLeastAdvisorRuleValue));

    if (this.data.cart) {
      this.cartId = this.data.cart.id;
    } else {
      this.store.select(CartSelectors.selectCurrentSalesOrderCart).subscribe((cart) => (this.cartId = cart?.id));
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  addToCart(selectedItem: FavoriteSku, quantity: number): void {
    this.store.dispatch(
      CartActions.addCartLineToCart({
        cartLine: {
          skuId: selectedItem.id,
          quantity,
          source: CartLineSources.FAVORITES,
        },
        cartId: this.cartId,
      }),
    );
  }

  openWizardDialog(selectedItem: FavoriteSku): void {
    this.store.dispatch(
      openWizardDialogForFavorite({
        favoriteSku: selectedItem,
        orderType: this.data.cart?.type,
        cartId: this.cartId,
      }),
    );
  }
  t;
  itemIsEligibleForCartType(favorite: FavoriteSku): boolean {
    return isItemTypeEligibleForCartType(favorite, this.data.cart?.type);
  }
  skuCanBeOrdered(sku: FavoriteSku): boolean {
    return skuIsOrderable(sku, this.hasAtLeastAdvisorRole);
  }

  public getUnorderableTranslationKey(sku: FavoriteSku): string {
    if (sku.orderingInformation === null || sku.packagingInformation === null) {
      return 'app.favorites.sku-unavailable-salesarea';
    }

    const userIsAuthorized = isUserAllowedToOrderItem(sku, this.hasAtLeastAdvisorRole);
    if (!userIsAuthorized && sku.orderingInformation.allowedUsers === AllowedOrderRoles.advisorsAndAbove) {
      return 'app.favorites.sku-no-authorization';
    }
    if (isItemTypeEligibleForCartType(sku, this.data.cart?.type)) {
      return 'sku-unsupported-ordertype';
    }
    return 'app.favorites.sku-not-purchasable';
  }

  public canBeOrdered = (sku: FavoriteSku): boolean =>
    skuIsOrderable(sku, this.hasAtLeastAdvisorRole) && isItemTypeEligibleForCartType(sku, this.data.cart?.type);
}
